import React from 'react';
import PropTypes from 'prop-types';

import { Button } from '@eventbrite/eds-button';
import { ValidationFormField } from '@eventbrite/eds-validation-form-field';
import { InputField } from '@eventbrite/eds-input-field';
import { STYLE_STATIC } from '@eventbrite/eds-input-field';

import CaptchaField from '../components/CaptchaField';
import {
    getRefundPolicyText,
    getOrderIdText,
    getOrganizerContactText,
    getNewRefundPolicyText,
    getNewOrganizerContactText,
} from '../constants/dynamicTexts';
import {
    ADDITIONAL_INFO_INPUT_LABEL,
    ADDITIONAL_INFO_INPUT_PLACEHOLDER,
    ADDITIONAL_INFO_INPUT_ANNOTATION,
    ATTENDEE_REFUND_CONDTIONS_TITLE,
    ATTENDEE_REFUND_FORM_TITLE,
    EMAIL_INPUT_LABEL,
    EMAIL_INPUT_ANNOTATION,
    ORDER_ID_INPUT_LABEL,
    SUBMIT_INPUT_LABEL,
    NEW_ADDITIONAL_INFO_INPUT_PLACEHOLDER,
    NEW_ORGANIZER_REFUND_POLICY_REQS_LINK,
    NEW_REFUND_ELIGIBILITY_LINK,
    ATTENDEE_REFUND_REQUEST_API_KEY_CONFIG,
} from '../constants';

import './RefundsForm.scss';

const HtmlInputAnnotation = ({ link }) => (
    <div
        className="eds-text-bs"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
            __html: getOrderIdText(link),
        }}
    ></div>
);

const InputAnnotation = ({ text }) => (
    <div className="eds-text-bs input-annotation">{text}</div>
);

const RefundFormSubHead = ({
    refundPolicyLink,
    refundInformationLink,
    newPostponedEventPolicy,
}) => (
    <div className="attendees-refund-form__subhead eds-text--left eds-text-bs">
        <p
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: newPostponedEventPolicy
                    ? getNewRefundPolicyText(refundPolicyLink).toString()
                    : getRefundPolicyText(refundPolicyLink).toString(),
            }}
        ></p>
        <p
            className="eds-l-mar-top-2"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
                __html: newPostponedEventPolicy
                    ? getNewOrganizerContactText(
                          refundInformationLink,
                      ).toString()
                    : getOrganizerContactText(refundInformationLink).toString(),
            }}
        ></p>
    </div>
);

export default class RefundsForm extends React.Component {
    static propTypes = {
        handleSubmit: PropTypes.func,
        learnMoreLink: PropTypes.string,
        recaptcha2PublicKey: PropTypes.string,
        refundInformationLink: PropTypes.string,
        featureFlags: PropTypes.shape({
            newPostponedEventPolicy: PropTypes.bool,
        }),
    };

    render() {
        const {
            addMainNotification,
            attendeeRefundRequirementsLink,
            clearRefundNotification,
            handleSubmit,
            hideCaptcha,
            invalid,
            learnMoreLink,
            recaptcha2PublicKey,
            refundInformationLink,
            refundNotificationOptions,
            shouldRenderRefundNotification,
            submitIsDisabled,
            featureFlags,
        } = this.props;

        if (shouldRenderRefundNotification) {
            const { message, type } = refundNotificationOptions;
            addMainNotification({
                type: type,
                children: message,
                isNotificationSticky: true,
            });
            clearRefundNotification();
        }

        return (
            <div className="eds-g-grid eds-align--center eds-l-mar-top-10 refuns-form-container">
                <div className="eds-g-cell eds-g-cell-md-6-12">
                    <form
                        onSubmit={handleSubmit}
                        data-spec="attendees-refund-form"
                    >
                        <div className="eds-text--center">
                            <h2 className="eds-text-hs">
                                {ATTENDEE_REFUND_FORM_TITLE}
                            </h2>
                        </div>
                        <div className="eds-g-group eds-align--center eds-l-mar-top-4">
                            <div className="eds-g-cell-md-9-12">
                                <RefundFormSubHead
                                    refundPolicyLink={
                                        featureFlags.newPostponedEventPolicy
                                            ? NEW_ORGANIZER_REFUND_POLICY_REQS_LINK
                                            : attendeeRefundRequirementsLink
                                    }
                                    refundInformationLink={
                                        featureFlags.newPostponedEventPolicy
                                            ? NEW_REFUND_ELIGIBILITY_LINK
                                            : refundInformationLink
                                    }
                                    newPostponedEventPolicy={
                                        featureFlags.newPostponedEventPolicy
                                    }
                                />
                                {!featureFlags.newPostponedEventPolicy && (
                                    <div className="attendees-refund-form__conditions eds-text-weight--heavy eds-text--left eds-text-bm">
                                        {ATTENDEE_REFUND_CONDTIONS_TITLE}
                                    </div>
                                )}
                                <div className="eds-g-cell eds-g-cell-6-12 eds-l-pad-right-2">
                                    <ValidationFormField
                                        v2={true}
                                        name="attendeeEmail"
                                    >
                                        <InputField
                                            id="attendeeEmail"
                                            name="attendeeEmail"
                                            label={EMAIL_INPUT_LABEL}
                                        />
                                    </ValidationFormField>
                                    <InputAnnotation
                                        text={EMAIL_INPUT_ANNOTATION}
                                    />
                                </div>
                                <div className="eds-g-cell eds-g-cell-6-12 eds-l-pad-left-2">
                                    <ValidationFormField
                                        v2={true}
                                        name="orderId"
                                    >
                                        <InputField
                                            id="orderId"
                                            name="orderId"
                                            label={ORDER_ID_INPUT_LABEL}
                                        />
                                    </ValidationFormField>
                                    <HtmlInputAnnotation link={learnMoreLink} />
                                </div>
                                <div className="eds-l-pad-top-4 refunds-form-additional-info">
                                    <ValidationFormField
                                        v2={true}
                                        name="additionalInformation"
                                    >
                                        <InputField
                                            id="additionalInformation"
                                            name="additionalInformation"
                                            style={STYLE_STATIC}
                                            isMultiline={true}
                                            rows={4}
                                            maxLength={255}
                                            hasCharacterCount={true}
                                            label={ADDITIONAL_INFO_INPUT_LABEL}
                                            placeholder={
                                                featureFlags.newPostponedEventPolicy
                                                    ? NEW_ADDITIONAL_INFO_INPUT_PLACEHOLDER
                                                    : ADDITIONAL_INFO_INPUT_PLACEHOLDER
                                            }
                                        />
                                    </ValidationFormField>
                                    {!featureFlags.newPostponedEventPolicy && (
                                        <div className="eds-l-pad-right-8">
                                            <InputAnnotation
                                                text={
                                                    ADDITIONAL_INFO_INPUT_ANNOTATION
                                                }
                                            />
                                        </div>
                                    )}
                                </div>
                                <CaptchaField
                                    recaptcha2PublicKey={recaptcha2PublicKey}
                                    hideCaptcha={hideCaptcha}
                                />
                                <div className="eds-g-group eds-align--center">
                                    <div className="eds-g-cell-md-7-12">
                                        <div className="eds-l-mar-vert-4 eds-text-bm">
                                            <Button
                                                type="submit"
                                                style="fill"
                                                size="block"
                                                disabled={
                                                    submitIsDisabled || invalid
                                                }
                                            >
                                                {SUBMIT_INPUT_LABEL}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
}
